import { Dispatch, SetStateAction, useRef, useState } from "react";
import {
  getTaskEditorBlockReorderIndex,
  isSameTaskEditorBlockDropPreview,
  reorderBlocks,
  TaskEditorBlock,
  TaskEditorBlockDropPreview,
} from "./taskEditorUtils";

type UseTaskEditorBlockDragArgs = {
  blocks: TaskEditorBlock[];
  setBlocks: Dispatch<SetStateAction<TaskEditorBlock[]>>;
  onBeforeReorder: () => void;
  cancelOtherDrag: () => void;
};

export type UseTaskEditorBlockDragReturn = {
  draggedBlockIndex: number | null;
  blockDropPreview: TaskEditorBlockDropPreview | null;
  handleBlockDragStart: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number
  ) => void;
  handleBlockDragOver: (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorBlockDropPreview
  ) => void;
  handleBlockDrop: (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorBlockDropPreview
  ) => void;
  handleBlockDragEnd: () => void;
  handleBlockCardDragOver: (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => void;
  handleBlockCardDrop: (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => void;
  cancelDrag: () => void;
};

const DRAG_MIME = "application/task-editor-block";

export function useTaskEditorBlockDrag({
  blocks,
  setBlocks,
  onBeforeReorder,
  cancelOtherDrag,
}: UseTaskEditorBlockDragArgs): UseTaskEditorBlockDragReturn {
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(
    null
  );
  const [blockDropPreview, setBlockDropPreview] =
    useState<TaskEditorBlockDropPreview | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const dropPreviewRef = useRef<TaskEditorBlockDropPreview | null>(null);
  const reorderedDuringDragRef = useRef(false);

  const cancelDrag = () => {
    draggedIndexRef.current = null;
    dropPreviewRef.current = null;
    reorderedDuringDragRef.current = false;
    setDraggedBlockIndex(null);
    setBlockDropPreview(null);
  };

  const handleBlockDragStart = (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number
  ) => {
    if (blockIndex < 0 || blockIndex >= blocks.length) {
      event.preventDefault();
      return;
    }
    cancelOtherDrag();
    draggedIndexRef.current = blockIndex;
    reorderedDuringDragRef.current = false;
    setDraggedBlockIndex(blockIndex);
    dropPreviewRef.current = null;
    setBlockDropPreview(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(DRAG_MIME, String(blockIndex));
  };

  const handleBlockDragOver = (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorBlockDropPreview
  ) => {
    const source = draggedIndexRef.current ?? draggedBlockIndex;
    if (source === null) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    setBlockDropPreview((current) => {
      const resolved = isSameTaskEditorBlockDropPreview(current, nextPreview)
        ? current
        : nextPreview;
      dropPreviewRef.current = resolved;
      return resolved;
    });

    const previewIndex = getTaskEditorBlockReorderIndex(
      blocks,
      source,
      nextPreview
    );
    if (previewIndex === null || previewIndex === source) return;

    if (!reorderedDuringDragRef.current) {
      onBeforeReorder();
      reorderedDuringDragRef.current = true;
    }

    setBlocks((currentBlocks) => {
      const currentSource = draggedIndexRef.current ?? source;
      const currentTarget = getTaskEditorBlockReorderIndex(
        currentBlocks,
        currentSource,
        nextPreview
      );
      if (
        currentTarget === null ||
        currentTarget === currentSource ||
        currentSource < 0 ||
        currentSource >= currentBlocks.length
      ) {
        return currentBlocks;
      }
      draggedIndexRef.current = currentTarget;
      setDraggedBlockIndex(currentTarget);
      return reorderBlocks(currentBlocks, currentSource, currentTarget);
    });
  };

  const getBlockCardDropPreview = (
    targetBlockIndex: number,
    event: React.DragEvent<HTMLElement>
  ): TaskEditorBlockDropPreview | null => {
    const source = draggedIndexRef.current ?? draggedBlockIndex;
    if (source === null || source === targetBlockIndex) return null;
    if (targetBlockIndex < 0 || targetBlockIndex >= blocks.length) return null;
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerY = event.clientY > 0 ? event.clientY : rect.bottom;
    const isBottomHalf = pointerY >= rect.top + rect.height / 2;
    return {
      insertionIndex: targetBlockIndex + (isBottomHalf ? 1 : 0),
    };
  };

  const handleBlockCardDragOver = (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => {
    const preview = getBlockCardDropPreview(targetBlockIndex, event);
    if (preview) handleBlockDragOver(event, preview);
  };

  const handleBlockCardDrop = (
    event: React.DragEvent<HTMLElement>,
    targetBlockIndex: number
  ) => {
    const preview = getBlockCardDropPreview(targetBlockIndex, event);
    if (preview) handleBlockDrop(event, preview);
  };

  const handleBlockDrop = (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorBlockDropPreview
  ) => {
    event.preventDefault();
    cancelDrag();
  };

  const handleBlockDragEnd = () => cancelDrag();

  return {
    draggedBlockIndex,
    blockDropPreview,
    handleBlockDragStart,
    handleBlockDragOver,
    handleBlockDrop,
    handleBlockDragEnd,
    handleBlockCardDragOver,
    handleBlockCardDrop,
    cancelDrag,
  };
}
